'use client'

import { ArrowUpIcon, EyeOffIcon, PlusIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { exchangeDisplayName, formatDuration } from '@/components/widget/market-status-badge'
import { useAddAccount } from '@/components/widget/no-account-cta'
import { VenueTradingNotice } from '@/components/widget/venue-trading-notice'
import { getExchangeIconKey } from '@/lib/configs/exchange-registry'
import { cn } from '@/lib/utils'
import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
import { useUserAccounts } from '@/service/account.hook'
import { isAlwaysOpen, useVenueStatus } from '@/service/venue-status.hook'
import {
  useConnectedVenueIssues,
  type VenueTradingIssue,
} from '@/service/venue-trading-status.hook'
import { useVenueHealthStream } from '@/service/ws/use-venue-health-stream'
import { useWebSocketContext } from '@/service/ws/websocket-context'
import { Account, AccountStatus } from '@/types/generated/common/account'
import { Exchange, TradeVenue } from '@/types/generated/common/types'
import { ExchangeStatusType, VenueHealth } from '@/types/generated/public_api'

const PING_INTERVAL_MS = 10000
const PING_TIMEOUT_MS = 1000

type ConnectionTone = 'connected' | 'connecting' | 'disconnected'

function getAccountConnectionTone({
  accounts,
  health,
  isAppConnected,
  isStatusError,
  isStatusPending,
  status,
}: {
  accounts: Account[]
  health: VenueHealth | null
  isAppConnected: boolean
  isStatusError: boolean
  isStatusPending: boolean
  status: ReturnType<typeof useVenueStatus>['data']
}): ConnectionTone {
  if (!isAppConnected) return 'disconnected'

  // Venue health is authoritative and independent from an individual
  // account's authentication lifecycle. A pending account must not mask a
  // healthy venue (and an active account must not mask a venue outage).
  if (health) {
    if (health.data !== ExchangeStatusType.EXCHANGE_STATUS_TYPE_OPEN) return 'disconnected'
    return health.trading === false ? 'disconnected' : 'connected'
  }

  const hasActiveAccount = accounts.some(
    (account) => account.status === AccountStatus.ACCOUNT_STATUS_ACTIVE
  )
  const hasConnectingAccount = accounts.some(
    (account) =>
      account.status === AccountStatus.ACCOUNT_STATUS_CREATING ||
      account.status === AccountStatus.ACCOUNT_STATUS_PENDING_AUTH
  )

  if (!hasActiveAccount) return hasConnectingAccount ? 'connecting' : 'disconnected'

  // Some environments do not emit an initial VenueHealth snapshot, and some
  // venues have no trading probe configured. Avoid leaving those rows in an
  // indefinite connecting state while still letting an explicit health event
  // override this market-status fallback immediately.
  if (isStatusPending) return 'connecting'
  if (isStatusError || !status) return 'disconnected'
  if (isAlwaysOpen(status)) return 'connected'
  return status.status === ExchangeStatusType.EXCHANGE_STATUS_TYPE_OPEN
    ? 'connected'
    : 'disconnected'
}

const ISSUE_STATUS_KEY = {
  disabled: 'statusDisabled',
  'trading-down': 'statusTradingDown',
  outage: 'statusOutage',
  maintenance: 'statusMaintenance',
} as const

function ConnectionStatusRow({
  accounts,
  health,
  isAppConnected,
  issues,
  now,
  tradeVenue,
}: {
  accounts: Account[]
  health: VenueHealth | null
  isAppConnected: boolean
  issues: readonly VenueTradingIssue[]
  now: number
  tradeVenue: TradeVenue
}) {
  const t = useTranslations('trade')
  const tIssue = useTranslations('venueTradingNotice')
  const exchange = useAccountPrimaryExchange(tradeVenue)
  const { data: status, isError, isPending } = useVenueStatus(exchange)
  const currentHealth = health?.exchange === exchange ? health : null
  // Why orders here would fail (routing switched off, failing trading probe, outage,
  // maintenance) — named in place of a bare "Disconnected", with what it means for orders.
  const issue = isAppConnected ? issues.find((item) => item.exchange === exchange) : undefined

  const tone = issue
    ? 'disconnected'
    : getAccountConnectionTone({
        accounts,
        health: currentHealth,
        isAppConnected,
        isStatusError: isError,
        isStatusPending: isPending,
        status,
      })
  const exchangeName = exchangeDisplayName(exchange)
  const iconKey = getExchangeIconKey(exchange) ?? exchangeName
  const boundaryMs = status?.nextChangeMs !== undefined ? Number(status.nextChangeMs) : undefined
  const venueHint =
    tone === 'disconnected' && boundaryMs !== undefined && boundaryMs > now
      ? formatDuration(boundaryMs - now)
      : null
  // Data-role host -> venue WS round-trip, NOT the client's own link. Absent
  // until the first beacon lands, and cleared by the beacon while a venue is
  // dark, so a stale pre-outage number never lingers.
  const latencyLabel =
    tone === 'connected'
      ? status?.latencyMs !== undefined
        ? `${status.latencyMs}ms`
        : t('latencyUnavailable')
      : '-'
  const latencyCellClass = cn(
    'truncate text-right whitespace-nowrap tabular-nums',
    tone === 'connected' ? 'text-white' : 'text-white/45'
  )

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={cn(
          // Last column fits a 4-digit "1234ms"; 44px clipped the "ms" off any
          // three-digit latency.
          'grid h-5 grid-cols-[minmax(0,1fr)_84px_56px] items-center gap-x-2 text-sm leading-none',
          tone === 'disconnected' && 'text-white/45'
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          {tone === 'disconnected' ? (
            <span className="flex size-5 shrink-0 items-center justify-center">
              <EyeOffIcon className="size-4 text-white/45" strokeWidth={2.25} />
            </span>
          ) : (
            <>
              <span
                className={cn(
                  'size-2 shrink-0 rounded-full',
                  tone === 'connected' && 'bg-chart-mint shadow-[0_0_8px_2px_rgba(0,255,160,0.45)]',
                  tone === 'connecting' && 'bg-ox-gold shadow-[0_0_8px_2px_rgba(219,194,75,0.35)]'
                )}
              />
              <ExchangeIcon
                exchange={iconKey}
                className="size-5 shrink-0 rounded-full opacity-90"
                logoObjectFit="contain"
                fallback={
                  <div className="flex h-full w-full items-center justify-center rounded-full bg-white/10 text-[9px] text-white/50">
                    {exchangeName[0]}
                  </div>
                }
              />
            </>
          )}
          <span className="min-w-0 truncate whitespace-nowrap text-white/90">{exchangeName}</span>
        </div>
        <span
          className={cn(
            'truncate font-medium whitespace-nowrap',
            tone === 'connected' && 'text-white',
            tone === 'connecting' && 'text-white',
            tone === 'disconnected' && 'text-white/45'
          )}
        >
          {issue
            ? tIssue(ISSUE_STATUS_KEY[issue.kind])
            : tone === 'connected'
              ? t('connected')
              : tone === 'connecting'
                ? t('connecting')
                : t('disconnected')}
        </span>
        {/* The countdown and the latency mean different things, so only the
          latency carries the venue-link tooltip. */}
        {venueHint ? (
          <span className={latencyCellClass}>{venueHint}</span>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={cn(latencyCellClass, 'cursor-help')}>{latencyLabel}</span>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[220px]">
              {t('venueLatencyTooltip')}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      {issue && <VenueTradingNotice issues={[issue]} />}
    </div>
  )
}

function VenueHealthSubscriber({
  isConnected,
  onHealth,
  tradeVenue,
}: {
  isConnected: boolean
  onHealth: (tradeVenue: TradeVenue, health: VenueHealth) => void
  tradeVenue: TradeVenue
}) {
  const exchange = useAccountPrimaryExchange(tradeVenue)
  const payload = useMemo(() => ({ exchange }), [exchange])

  useVenueHealthStream({
    connected: isConnected && exchange !== Exchange.EXCHANGE_UNSPECIFIED,
    payload,
    onMessage: (health) => {
      if (health?.exchange === exchange) onHealth(tradeVenue, health)
    },
  })

  return null
}

/** Round-trip to the API, pinged from a worker so a busy main thread doesn't skew it. */
function usePingLatency() {
  const [latencyMs, setLatencyMs] = useState<number | null>(null)
  const workerRef = useRef<Worker | null>(null)

  useEffect(() => {
    const worker = new Worker(new URL('@/lib/workers/ping.worker.ts', import.meta.url))
    workerRef.current = worker

    worker.onmessage = (e) => {
      if (e.data.type === 'LATENCY') {
        setLatencyMs(e.data.value)
      }
    }

    worker.postMessage({
      type: 'START',
      apiUrl: process.env.NEXT_PUBLIC_API_URL,
      intervalMs: PING_INTERVAL_MS,
      timeoutMs: PING_TIMEOUT_MS,
    })

    return () => {
      worker.postMessage({ type: 'STOP' })
      worker.terminate()
      workerRef.current = null
    }
  }, [])

  return latencyMs
}

/** The app's own link: whether the WebSocket is up, and the API round-trip while it is. */
export function useAppConnection() {
  const { isConnected } = useWebSocketContext()
  const latencyMs = usePingLatency()
  return { isConnected, latencyMs }
}

/** The status light: mint with a glow while connected, red otherwise. */
export function ConnectionDot({
  isConnected,
  issues = [],
  className,
}: {
  isConnected: boolean
  /** Connected venues that can't take orders: gold (warning) or red (orders blocked). */
  issues?: readonly VenueTradingIssue[]
  className?: string
}) {
  const blocked = issues.some((issue) => issue.blocksOrders)
  return (
    <span
      aria-hidden
      className={cn(
        'size-1.5 shrink-0 rounded-full',
        !isConnected || blocked
          ? 'bg-chart-red'
          : issues.length > 0
            ? 'bg-ox-gold'
            : 'bg-chart-mint shadow-[0_0_4px_1px_rgba(0,255,160,0.5)]',
        className
      )}
    />
  )
}

/** "Connected · 23ms" / "Disconnected", tinted by state. */
export function ConnectionLabel({
  isConnected,
  latencyMs,
  issues = [],
}: {
  isConnected: boolean
  latencyMs: number | null
  /** Connected venues that can't take orders — named here instead of a plain "Connected". */
  issues?: readonly VenueTradingIssue[]
}) {
  const t = useTranslations('trade')
  const tIssue = useTranslations('venueTradingNotice')
  if (isConnected && issues.length > 0) {
    const blocked = issues.some((issue) => issue.blocksOrders)
    return (
      <span
        className={cn(
          'min-w-0 truncate whitespace-nowrap',
          blocked ? 'text-chart-red' : 'text-ox-gold'
        )}
      >
        {issues.length === 1
          ? tIssue('triggerOne', {
              exchange: exchangeDisplayName(issues[0].exchange),
              status: tIssue(ISSUE_STATUS_KEY[issues[0].kind]),
            })
          : tIssue('triggerMany', { count: issues.length })}
      </span>
    )
  }
  return (
    <span className="flex min-w-0 items-baseline gap-1.5 whitespace-nowrap">
      <span className={isConnected ? 'text-chart-mint' : 'text-chart-red'}>
        {isConnected ? t('connected') : t('disconnected')}
      </span>
      {isConnected && latencyMs !== null && (
        <span className="text-chart-mint/60 text-[11px] tabular-nums">{latencyMs}ms</span>
      )}
    </span>
  )
}

/**
 * Hovering `children` shows every connected venue's state (connected / connecting / down, with
 * the venue link's latency or the time until it reopens) and an Add account action. The venue
 * health streams stay subscribed while this is mounted, so the card opens already current.
 */
export function ConnectionStatusHoverCard({
  isConnected,
  side = 'right',
  align = 'end',
  children,
}: {
  isConnected: boolean
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  children: ReactElement
}) {
  const t = useTranslations('trade')
  const accountSelectorT = useTranslations('accountSelector')
  const { data: accountsData } = useUserAccounts()
  const {
    atAccountCap,
    addAccount: handleAddAccountClick,
    sheet: addAccountSheet,
  } = useAddAccount()
  const accountGroups = useMemo(() => {
    const accounts = accountsData?.accounts ?? []
    const groups = new Map<TradeVenue, Account[]>()
    for (const account of accounts) {
      const group = groups.get(account.tradeVenue)
      if (group) {
        group.push(account)
      } else {
        groups.set(account.tradeVenue, [account])
      }
    }
    return Array.from(groups.entries()).map(([tradeVenue, groupedAccounts]) => ({
      accounts: groupedAccounts,
      tradeVenue,
    }))
  }, [accountsData?.accounts])
  const [healthByVenue, setHealthByVenue] = useState<Map<TradeVenue, VenueHealth>>(new Map())
  const handleHealth = useCallback((tradeVenue: TradeVenue, health: VenueHealth) => {
    setHealthByVenue((current) => {
      const previous = current.get(tradeVenue)
      if (
        previous?.exchange === health.exchange &&
        previous.data === health.data &&
        previous.trading === health.trading
      ) {
        return current
      }
      const next = new Map(current)
      next.set(tradeVenue, health)
      return next
    })
  }, [])
  const venueIssues = useConnectedVenueIssues()
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(interval)
  }, [])

  return (
    <>
      {accountGroups.map(({ tradeVenue }) => (
        <VenueHealthSubscriber
          key={tradeVenue}
          isConnected={isConnected}
          onHealth={handleHealth}
          tradeVenue={tradeVenue}
        />
      ))}
      <HoverCard openDelay={120} closeDelay={120}>
        <HoverCardTrigger asChild>{children}</HoverCardTrigger>
        <HoverCardContent
          side={side}
          align={align}
          sideOffset={10}
          className="text-foreground z-[70] w-[340px] p-4"
        >
          <div className="flex flex-col gap-4">
            <span className="text-xs leading-[1.2] font-medium tracking-[-0.02em] text-white/60">
              {t('connectionStatus')}
            </span>
            <div className="flex flex-col gap-2.5">
              {accountGroups.length > 0 ? (
                accountGroups.map(({ accounts: groupedAccounts, tradeVenue }) => (
                  <ConnectionStatusRow
                    key={tradeVenue}
                    accounts={groupedAccounts}
                    health={healthByVenue.get(tradeVenue) ?? null}
                    isAppConnected={isConnected}
                    issues={venueIssues}
                    now={now}
                    tradeVenue={tradeVenue}
                  />
                ))
              ) : (
                <div className="text-sm text-white/45">{t('noConnectedAccounts')}</div>
              )}
            </div>
            <button
              type="button"
              onClick={handleAddAccountClick}
              className="flex cursor-pointer items-center gap-1.5 border-t border-white/10 pt-3 text-xs font-medium text-white/70 transition-colors hover:text-white"
            >
              {atAccountCap ? (
                <ArrowUpIcon className="size-3.5 shrink-0" />
              ) : (
                <PlusIcon className="size-3.5 shrink-0" />
              )}
              {atAccountCap
                ? accountSelectorT('accountLimitReachedUpgrade')
                : accountSelectorT('addNewAccount')}
            </button>
          </div>
        </HoverCardContent>
      </HoverCard>
      {addAccountSheet}
    </>
  )
}
